/**
 * Background — full tavern interior rendered as inline SVG.
 *
 * Per docs/ui/menu-screens.md §8. Replaces the previous black-void
 * implementation that anchored a 1440×900 viewBox to the bottom and
 * sliced 70% of the art off-screen. Now uses an 800×1200 portrait
 * viewBox with `xMidYMid slice` so the focal point (hearth + bar) is
 * always visible regardless of viewport aspect.
 *
 * Layers (back to front):
 *   1. Sky/wall gradient (bgSky)
 *   2. Stone wall pattern
 *   3. Plank wainscoting
 *   4. Plank floor with perspective
 *   5. Hearth surround + cavity + logs + 3-layer flames + halo
 *   6. Bar counter + back-shelf + bottles + tankards + candle
 *   7. Hanging lanterns (3)
 *   8. Wall sconces (2)
 *   9. Pendulum clock + framed picture + deer skull
 *  10. Foreground table edge (camera-depth context)
 *  11. Fog gradient at top
 *  12. Per-mode accent (gate/select/home/locker/onboarding)
 *  13. Ember canvas (drifting particles, kept from prior version)
 *
 * All ambient animation is SVG SMIL `<animate>`; the React component
 * stays a pure render. `prefers-reduced-motion` is honored via a
 * `<style>` block inside the SVG that freezes flames + the pendulum.
 */
import { useEffect, useRef } from 'react';

interface Props {
  mode?: 'default' | 'gate' | 'select' | 'home' | 'locker' | 'onboarding';
}

export function Background({ mode = 'default' }: Props = {}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Drifting ember canvas — same particle system as before, retained.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduced) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = canvas.width = window.innerWidth * Math.min(2, window.devicePixelRatio ?? 1);
    let h = canvas.height = window.innerHeight * Math.min(2, window.devicePixelRatio ?? 1);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;

    interface Ember { x: number; y: number; vx: number; vy: number; r: number; life: number; max: number; hue: number }
    const embers: Ember[] = [];
    const targetCount = Math.min(80, Math.max(30, Math.floor((window.innerWidth * window.innerHeight) / 16000)));

    function spawn(): Ember {
      const hue = 18 + Math.random() * 40;
      return {
        x: Math.random() * w,
        y: h + Math.random() * 40,
        vx: (Math.random() - 0.5) * 0.4,
        vy: -(0.3 + Math.random() * 0.7),
        r: 0.6 + Math.random() * 1.8,
        life: 0,
        max: 280 + Math.random() * 320,
        hue,
      };
    }

    for (let i = 0; i < targetCount; i++) {
      const e = spawn();
      e.y = Math.random() * h;
      e.life = Math.random() * e.max;
      embers.push(e);
    }

    let raf = 0;
    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i < embers.length; i++) {
        const e = embers[i]!;
        e.x += e.vx;
        e.y += e.vy;
        e.vy -= 0.006;
        e.vx += (Math.random() - 0.5) * 0.08;
        e.life++;
        const a = Math.max(0, 1 - e.life / e.max) * 0.55;
        ctx.beginPath();
        ctx.fillStyle = `hsla(${e.hue}, 80%, 60%, ${a})`;
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.fillStyle = `hsla(${e.hue}, 80%, 70%, ${a * 0.4})`;
        ctx.arc(e.x, e.y, e.r * 2.5, 0, Math.PI * 2);
        ctx.fill();
        if (e.life >= e.max || e.y < -20) {
          embers[i] = spawn();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onResize = () => {
      w = canvas.width = window.innerWidth * Math.min(2, window.devicePixelRatio ?? 1);
      h = canvas.height = window.innerHeight * Math.min(2, window.devicePixelRatio ?? 1);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className={`bg-fx bg-fx-${mode}`} aria-hidden="true">
      <svg
        className="bg-tavern"
        viewBox="0 0 800 1200"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* ─── Sky/wall vertical gradient ─────────────────────── */}
          <linearGradient id="bgSky" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#1a0e1f" />
            <stop offset="40%" stopColor="#2a1a2a" />
            <stop offset="80%" stopColor="#3a2418" />
            <stop offset="100%" stopColor="#1a0e08" />
          </linearGradient>

          {/* ─── Stone block pattern (2026-05-16 quality pass) ──
           *  Varied stone sizes, two-tone mortar, occasional chipped
           *  corner, surface speckles. Reads as hand-laid masonry. */}
          <pattern id="stoneBlock" x="0" y="0" width="160" height="80" patternUnits="userSpaceOnUse">
            <rect width="160" height="80" fill="#1a141c" />
            <rect width="160" height="80" fill="#0a0810" opacity="0.35" />
            {/* Row 1 */}
            <rect x="2"   y="2"  width="60" height="34" fill="#322438" stroke="#070510" strokeWidth="2" rx="2" />
            <rect x="66"  y="2"  width="44" height="34" fill="#2a1e30" stroke="#070510" strokeWidth="2" rx="2" />
            <rect x="114" y="2"  width="44" height="34" fill="#352840" stroke="#070510" strokeWidth="2" rx="2" />
            {/* Chip on corner of stone 1 */}
            <path d="M 2 30 L 12 34 L 6 36 Z" fill="#0a0810" opacity="0.7" />
            <line x1="6"   y1="6"  x2="58"  y2="6"  stroke="#4a3a55" strokeWidth="0.6" opacity="0.55" />
            <line x1="70"  y1="6"  x2="106" y2="6"  stroke="#3a2c45" strokeWidth="0.6" opacity="0.55" />
            <line x1="118" y1="6"  x2="154" y2="6"  stroke="#4a3a55" strokeWidth="0.6" opacity="0.55" />
            {/* Row 2 — offset */}
            <rect x="-28" y="42" width="60" height="36" fill="#2c2034" stroke="#070510" strokeWidth="2" rx="2" />
            <rect x="36"  y="42" width="50" height="36" fill="#382a48" stroke="#070510" strokeWidth="2" rx="2" />
            <rect x="90"  y="42" width="38" height="36" fill="#241828" stroke="#070510" strokeWidth="2" rx="2" />
            <rect x="132" y="42" width="60" height="36" fill="#2e2238" stroke="#070510" strokeWidth="2" rx="2" />
            {/* Speckles */}
            <circle cx="28"  cy="18" r="0.8" fill="#5a4a65" opacity="0.6" />
            <circle cx="86"  cy="20" r="0.8" fill="#5a4a65" opacity="0.5" />
            <circle cx="132" cy="14" r="0.8" fill="#5a4a65" opacity="0.55" />
            <circle cx="50"  cy="58" r="0.8" fill="#5a4a65" opacity="0.5" />
            <circle cx="108" cy="62" r="0.8" fill="#5a4a65" opacity="0.55" />
          </pattern>

          {/* ─── Plank wainscoting pattern (2026-05-16 quality pass)
           *  Per-plank color variation, knots with concentric rings,
           *  grain striations, bevel highlights on each plank edge. */}
          <pattern id="plankWall" x="0" y="0" width="128" height="320" patternUnits="userSpaceOnUse">
            <rect width="128" height="320" fill="#1a0e08" />
            {/* 4 planks, alternating tones */}
            <rect x="0"   y="0" width="32" height="320" fill="#3a2614" />
            <rect x="32"  y="0" width="32" height="320" fill="#2a1810" />
            <rect x="64"  y="0" width="32" height="320" fill="#322012" />
            <rect x="96"  y="0" width="32" height="320" fill="#26160c" />
            {/* Plank seams */}
            <line x1="0"   y1="0" x2="0"   y2="320" stroke="#080402" strokeWidth="1.4" />
            <line x1="32"  y1="0" x2="32"  y2="320" stroke="#080402" strokeWidth="1.4" />
            <line x1="64"  y1="0" x2="64"  y2="320" stroke="#080402" strokeWidth="1.4" />
            <line x1="96"  y1="0" x2="96"  y2="320" stroke="#080402" strokeWidth="1.4" />
            {/* Bevel highlight on left edge of each plank */}
            <line x1="2"   y1="0" x2="2"   y2="320" stroke="#5b3a1f" strokeWidth="0.6" opacity="0.55" />
            <line x1="34"  y1="0" x2="34"  y2="320" stroke="#4a2e18" strokeWidth="0.6" opacity="0.45" />
            <line x1="66"  y1="0" x2="66"  y2="320" stroke="#5b3a1f" strokeWidth="0.6" opacity="0.5" />
            <line x1="98"  y1="0" x2="98"  y2="320" stroke="#4a2e18" strokeWidth="0.6" opacity="0.4" />
            {/* Grain striations */}
            <line x1="14"  y1="0" x2="14"  y2="320" stroke="#1a0e08" strokeWidth="0.4" opacity="0.6" />
            <line x1="22"  y1="0" x2="22"  y2="320" stroke="#1a0e08" strokeWidth="0.3" opacity="0.4" />
            <line x1="48"  y1="0" x2="48"  y2="320" stroke="#1a0e08" strokeWidth="0.4" opacity="0.55" />
            <line x1="78"  y1="0" x2="78"  y2="320" stroke="#1a0e08" strokeWidth="0.4" opacity="0.6" />
            <line x1="110" y1="0" x2="110" y2="320" stroke="#1a0e08" strokeWidth="0.4" opacity="0.5" />
            {/* Knots — concentric ring details */}
            <g opacity="0.85">
              <circle cx="16"  cy="62"  r="3.5" fill="#0a0604" />
              <ellipse cx="16" cy="62"  rx="6"  ry="2.5" fill="none" stroke="#1a0e08" strokeWidth="0.6" />
              <circle cx="80"  cy="148" r="3"   fill="#0a0604" />
              <ellipse cx="80" cy="148" rx="5.5" ry="2" fill="none" stroke="#1a0e08" strokeWidth="0.6" />
              <circle cx="48"  cy="248" r="2.5" fill="#0a0604" />
              <ellipse cx="48" cy="248" rx="4.5" ry="1.6" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
              <circle cx="108" cy="38"  r="2.8" fill="#0a0604" />
              <ellipse cx="108" cy="38" rx="5"  ry="1.8" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
              <circle cx="22"  cy="210" r="2.6" fill="#0a0604" />
              <ellipse cx="22"  cy="210" rx="4.8" ry="1.7" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
            </g>
          </pattern>

          {/* ─── Plank floor pattern (2026-05-16 quality pass) ────
           *  Per-row tonal variation, knots, staggered end-cap board
           *  breaks, bevel highlights. */}
          <pattern id="plankFloor" x="0" y="0" width="240" height="112" patternUnits="userSpaceOnUse">
            <rect width="240" height="112" fill="#1a0e08" />
            {/* 4 plank rows */}
            <rect x="0" y="0"  width="240" height="28" fill="#3a2410" />
            <rect x="0" y="28" width="240" height="28" fill="#28180e" />
            <rect x="0" y="56" width="240" height="28" fill="#322012" />
            <rect x="0" y="84" width="240" height="28" fill="#26170c" />
            {/* Seams */}
            <line x1="0" y1="0"   x2="240" y2="0"   stroke="#080402" strokeWidth="1.4" />
            <line x1="0" y1="28"  x2="240" y2="28"  stroke="#080402" strokeWidth="1.4" />
            <line x1="0" y1="56"  x2="240" y2="56"  stroke="#080402" strokeWidth="1.4" />
            <line x1="0" y1="84"  x2="240" y2="84"  stroke="#080402" strokeWidth="1.4" />
            <line x1="0" y1="2"   x2="240" y2="2"   stroke="#5b3a1f" strokeWidth="0.6" opacity="0.45" />
            <line x1="0" y1="30"  x2="240" y2="30"  stroke="#4a2e18" strokeWidth="0.6" opacity="0.4" />
            <line x1="0" y1="58"  x2="240" y2="58"  stroke="#5b3a1f" strokeWidth="0.6" opacity="0.45" />
            <line x1="0" y1="86"  x2="240" y2="86"  stroke="#4a2e18" strokeWidth="0.6" opacity="0.4" />
            {/* End-cap board breaks */}
            <line x1="78"  y1="0"  x2="78"  y2="28"  stroke="#080402" strokeWidth="1" />
            <line x1="162" y1="28" x2="162" y2="56"  stroke="#080402" strokeWidth="1" />
            <line x1="48"  y1="56" x2="48"  y2="84"  stroke="#080402" strokeWidth="1" />
            <line x1="180" y1="84" x2="180" y2="112" stroke="#080402" strokeWidth="1" />
            {/* Knots */}
            <g opacity="0.8">
              <circle cx="50"  cy="14" r="2.5" fill="#0a0604" />
              <ellipse cx="50" cy="14" rx="4.5" ry="1.6" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
              <circle cx="190" cy="42" r="2"   fill="#0a0604" />
              <ellipse cx="190" cy="42" rx="3.5" ry="1.4" fill="none" stroke="#1a0e08" strokeWidth="0.5" />
              <circle cx="106" cy="70" r="2.4" fill="#0a0604" />
              <circle cx="220" cy="98" r="2"   fill="#0a0604" />
            </g>
          </pattern>

          {/* ─── Hearth flame radial gradient ───────────────────── */}
          <radialGradient id="hearthGlow" cx="0.5" cy="0.7" r="0.7">
            <stop offset="0%"   stopColor="#ffd28a" stopOpacity="0.9" />
            <stop offset="40%"  stopColor="#ff8c42" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#ff4a2a" stopOpacity="0" />
          </radialGradient>

          {/* ─── Hearth halo on the floor ───────────────────────── */}
          <radialGradient id="hearthHalo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%"   stopColor="#ff8c42" stopOpacity="0.35" />
            <stop offset="60%"  stopColor="#c25516" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#c25516" stopOpacity="0" />
          </radialGradient>

          {/* ─── Lantern halo gradient ──────────────────────────── */}
          <radialGradient id="lanternHalo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%"   stopColor="#ffb27a" stopOpacity="0.42" />
            <stop offset="60%"  stopColor="#ff8c42" stopOpacity="0.14" />
            <stop offset="100%" stopColor="#ff8c42" stopOpacity="0" />
          </radialGradient>

          {/* ─── Sconce halo ────────────────────────────────────── */}
          <radialGradient id="sconceHalo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%"   stopColor="#ffb27a" stopOpacity="0.3" />
            <stop offset="80%"  stopColor="#ff8c42" stopOpacity="0" />
          </radialGradient>

          {/* ─── Top fog ────────────────────────────────────────── */}
          <linearGradient id="bgTopFog" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"   stopColor="rgba(10,8,20,0.85)" />
            <stop offset="60%"  stopColor="rgba(10,8,20,0.35)" />
            <stop offset="100%" stopColor="rgba(10,8,20,0)" />
          </linearGradient>

          {/* ─── Brass gradient ─────────────────────────────────── */}
          <linearGradient id="brass" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#f5c14a" />
            <stop offset="50%" stopColor="#a87a3a" />
            <stop offset="100%" stopColor="#5a3818" />
          </linearGradient>

          {/* ─── Wood gradient (frames, beams) ──────────────────── */}
          <linearGradient id="oak" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#5b3a1f" />
            <stop offset="60%" stopColor="#3a2410" />
            <stop offset="100%" stopColor="#1a0e08" />
          </linearGradient>

          {/* ─── Bottle glass (back-shelf) ──────────────────────── */}
          <linearGradient id="bottleGreen" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#2a4a2a" />
            <stop offset="100%" stopColor="#0a1a0a" />
          </linearGradient>
          <linearGradient id="bottleAmber" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#a87a3a" />
            <stop offset="100%" stopColor="#3a2410" />
          </linearGradient>
          <linearGradient id="bottleClear" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#5a4a4a" />
            <stop offset="100%" stopColor="#1a0e0a" />
          </linearGradient>

          {/* ─── Reduced-motion freeze rules embedded in SVG ────── */}
          <style>
            {`@media (prefers-reduced-motion: reduce) {
              .bg-tavern animate, .bg-tavern animateTransform { display: none; }
              .bg-flame-core { opacity: 0.85 !important; }
              .bg-flame-mid  { opacity: 0.7  !important; }
              .bg-flame-out  { opacity: 0.5  !important; }
              .bg-pendulum   { transform: none !important; }
            }`}
          </style>
        </defs>

        {/* Layer 1: sky/wall vertical gradient. */}
        <rect x="0" y="0" width="800" height="1200" fill="url(#bgSky)" />

        {/* Layer 2: stone wall covering top half. */}
        <rect x="0" y="0" width="800" height="580" fill="url(#stoneBlock)" />
        {/* Stone subtle grime overlay. */}
        <rect x="0" y="0" width="800" height="580" fill="rgba(0,0,0,0.18)" />

        {/* Layer 3: plank wainscoting (mid-wall). */}
        <rect x="0" y="400" width="800" height="320" fill="url(#plankWall)" />
        {/* Wainscoting top rail. */}
        <rect x="0" y="396" width="800" height="8" fill="#5b3a1f" />
        <rect x="0" y="394" width="800" height="2" fill="#7a4a28" />
        {/* Wainscoting cap rail (chair-rail). */}
        <rect x="0" y="556" width="800" height="6" fill="#3a2410" />
        <rect x="0" y="554" width="800" height="2" fill="#5b3a1f" />

        {/* Layer 4: plank floor (with perspective implied via darker far rows). */}
        <rect x="0" y="720" width="800" height="480" fill="url(#plankFloor)" />
        {/* Far-row darkening to imply perspective receding to vanishing point ~y=600. */}
        <rect x="0" y="720" width="800" height="60"  fill="rgba(0,0,0,0.4)" />
        <rect x="0" y="780" width="800" height="40"  fill="rgba(0,0,0,0.25)" />
        <rect x="0" y="820" width="800" height="40"  fill="rgba(0,0,0,0.12)" />

        {/* ─── Layer 5: HEARTH (2026-05-16 quality pass) ────────────
         *  Now with: brick masonry stones around the arch, decorative
         *  corbels, iron grate bars, log end-grain rings, individual
         *  hot embers, smoke wisps rising above the flame, and a
         *  volumetric warm light cone spreading onto the floor. */}
        <g className="bg-hearth" opacity="0.6">
          {/* Volumetric light cone — soft trapezoid from firebox onto floor */}
          <path d="M 320 610 L 480 610 L 600 900 L 200 900 Z" fill="url(#hearthHalo)" opacity="0.35" />
          <ellipse cx="400" cy="740" rx="240" ry="100" fill="url(#hearthHalo)" opacity="0.55">
            <animate attributeName="rx" values="240;270;250;240" dur="4s" repeatCount="indefinite" />
          </ellipse>

          {/* Hearth stone surround — arched outer */}
          <path
            d="M 290 720 L 290 460 Q 290 320 400 320 Q 510 320 510 460 L 510 720 Z"
            fill="#1a1218"
            stroke="#0a0608"
            strokeWidth="3"
          />
          {/* Brick-masonry on the arch face — individual carved stones */}
          <g opacity="0.85" stroke="#0a0608" strokeWidth="1.2">
            <path d="M 290 720 L 290 620 L 308 620 L 308 720 Z" fill="#2a1f2a" />
            <path d="M 290 620 L 290 540 L 308 542 L 308 620 Z" fill="#231828" />
            <path d="M 290 540 L 290 460 L 308 462 L 308 540 Z" fill="#2a1f2a" />
            <path d="M 290 460 Q 296 380 340 340 L 354 354 Q 314 388 308 462 Z" fill="#251a2a" />
            <path d="M 340 340 Q 370 320 400 320 L 400 336 Q 372 336 354 354 Z" fill="#2a1f2a" />
            <path d="M 400 320 Q 430 320 460 340 L 446 354 Q 428 336 400 336 Z" fill="#251a2a" />
            <path d="M 460 340 Q 504 380 510 460 L 492 462 Q 486 388 446 354 Z" fill="#2a1f2a" />
            <path d="M 510 460 L 510 540 L 492 540 L 492 462 Z" fill="#231828" />
            <path d="M 510 540 L 510 620 L 492 620 L 492 540 Z" fill="#2a1f2a" />
            <path d="M 510 620 L 510 720 L 492 720 L 492 620 Z" fill="#231828" />
          </g>
          {/* Inner outline (was already there) */}
          <path
            d="M 310 720 L 310 480 Q 310 350 400 350 Q 490 350 490 480 L 490 720 Z"
            fill="none"
            stroke="#2a1a20"
            strokeWidth="1.5"
          />

          {/* Wood beam mantel with grain + decorative corbels */}
          <rect x="270" y="304" width="260" height="22" fill="#3a2410" stroke="#1a0e08" strokeWidth="1.5" />
          <rect x="266" y="298" width="268" height="8"  fill="#5b3a1f" />
          <line x1="270" y1="312" x2="530" y2="312" stroke="#5b3a1f" strokeWidth="0.5" opacity="0.5" />
          <line x1="270" y1="318" x2="530" y2="318" stroke="#1a0e08" strokeWidth="0.4" opacity="0.6" />
          <path d="M 272 326 L 302 326 L 292 350 L 280 350 Z" fill="#2a1810" stroke="#0a0604" strokeWidth="1" />
          <path d="M 498 326 L 528 326 L 520 350 L 508 350 Z" fill="#2a1810" stroke="#0a0604" strokeWidth="1" />

          {/* Inner firebox (deep shadow) */}
          <rect x="328" y="440" width="144" height="270" fill="#080406" />
          <path
            d="M 332 440 L 332 700 L 466 700 L 466 440"
            fill="none" stroke="#1a0e08" strokeWidth="2"
          />
          {/* Iron grate bars (vertical) */}
          <g opacity="0.85" stroke="#1a1218" strokeWidth="1.5">
            <line x1="342" y1="660" x2="342" y2="700" />
            <line x1="362" y1="660" x2="362" y2="700" />
            <line x1="382" y1="660" x2="382" y2="700" />
            <line x1="402" y1="660" x2="402" y2="700" />
            <line x1="422" y1="660" x2="422" y2="700" />
            <line x1="442" y1="660" x2="442" y2="700" />
            <line x1="462" y1="660" x2="462" y2="700" />
          </g>
          <line x1="332" y1="660" x2="468" y2="660" stroke="#1a1218" strokeWidth="2" />

          {/* Logs (3 stacked) with end-grain rings */}
          <ellipse cx="370" cy="680" rx="30" ry="8" fill="#3a2410" />
          <ellipse cx="370" cy="680" rx="6" ry="6" fill="#1a0e08" />
          <ellipse cx="430" cy="680" rx="30" ry="8" fill="#3a2410" />
          <ellipse cx="430" cy="680" rx="6" ry="6" fill="#1a0e08" />
          <ellipse cx="400" cy="668" rx="36" ry="8" fill="#5b3a1f" />
          <ellipse cx="400" cy="668" rx="7" ry="7" fill="#1a0e08" />

          {/* Ember bed */}
          <ellipse cx="400" cy="675" rx="44" ry="9" fill="#ff4a2a" opacity="0.45">
            <animate attributeName="opacity" values="0.4;0.6;0.45" dur="1.7s" repeatCount="indefinite" />
          </ellipse>
          {/* Individual hot embers */}
          <circle cx="370" cy="688" r="2" fill="#ffd28a" opacity="0.85">
            <animate attributeName="opacity" values="0.6;1;0.7" dur="1.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="400" cy="692" r="2.4" fill="#ffd28a" opacity="0.9">
            <animate attributeName="opacity" values="0.7;1;0.8" dur="1.1s" repeatCount="indefinite" />
          </circle>
          <circle cx="430" cy="686" r="1.8" fill="#ffd28a" opacity="0.85">
            <animate attributeName="opacity" values="0.55;0.95;0.7" dur="1.6s" repeatCount="indefinite" />
          </circle>

          {/* Flame layers */}
          <ellipse className="bg-flame-out" cx="400" cy="610" rx="50" ry="80" fill="#ff8c42" opacity="0.32">
            <animate attributeName="opacity" values="0.26;0.42;0.32" dur="1.3s" repeatCount="indefinite" />
            <animate attributeName="ry" values="80;90;76;80" dur="1.3s" repeatCount="indefinite" />
          </ellipse>
          <ellipse className="bg-flame-mid" cx="400" cy="610" rx="32" ry="64" fill="#ffb27a" opacity="0.45">
            <animate attributeName="opacity" values="0.38;0.58;0.45" dur="0.9s" repeatCount="indefinite" />
            <animate attributeName="ry" values="64;72;60;64" dur="0.9s" repeatCount="indefinite" />
          </ellipse>
          <ellipse className="bg-flame-core" cx="400" cy="618" rx="18" ry="46" fill="#fff7c2" opacity="0.55">
            <animate attributeName="opacity" values="0.45;0.7;0.55;0.5" dur="0.6s" repeatCount="indefinite" />
            <animate attributeName="ry" values="46;52;42;46" dur="0.6s" repeatCount="indefinite" />
          </ellipse>

          {/* Inner halo */}
          <ellipse cx="400" cy="610" rx="60" ry="90" fill="url(#hearthGlow)" opacity="0.55" />

          {/* Smoke wisps rising above the flame */}
          <g opacity="0.18">
            <ellipse cx="394" cy="520" rx="10" ry="14" fill="#5a4a4a">
              <animate attributeName="opacity" values="0;0.18;0" dur="3.8s" repeatCount="indefinite" />
              <animate attributeName="cy" values="520;470;420" dur="3.8s" repeatCount="indefinite" />
            </ellipse>
            <ellipse cx="406" cy="510" rx="9" ry="12" fill="#5a4a4a">
              <animate attributeName="opacity" values="0;0.16;0" dur="4.6s" begin="1.2s" repeatCount="indefinite" />
              <animate attributeName="cy" values="510;460;400" dur="4.6s" begin="1.2s" repeatCount="indefinite" />
            </ellipse>
          </g>

          {/* Cast-iron hook over the firebox */}
          <line x1="400" y1="326" x2="400" y2="380" stroke="#0a0608" strokeWidth="2" />
          <path d="M 400 380 Q 400 392 410 392 Q 420 392 420 382" fill="none" stroke="#0a0608" strokeWidth="2" />
        </g>

        {/* ─── Layer 6: BAR COUNTER (right edge) ─────────────────────── */}
        <g className="bg-bar">
          {/* Bar back-shelf cabinet. */}
          <rect x="600" y="380" width="200" height="240" fill="#2a1a10" stroke="#0a0604" strokeWidth="2" />
          {/* Shelf horizontal dividers. */}
          <line x1="600" y1="440" x2="800" y2="440" stroke="#5b3a1f" strokeWidth="2" />
          <line x1="600" y1="500" x2="800" y2="500" stroke="#5b3a1f" strokeWidth="2" />
          <line x1="600" y1="560" x2="800" y2="560" stroke="#5b3a1f" strokeWidth="2" />

          {/* Bottles on each shelf — detailed (neck + cork + body +
           *  parchment label + reflection glint). 2026-05-16 quality
           *  pass replaces the previous flat rectangle silhouettes. */}
          {/* Top shelf */}
          {[
            { x: 616, h: 42, c: 'bottleGreen' as const, label: true  },
            { x: 638, h: 36, c: 'bottleAmber' as const, label: false },
            { x: 660, h: 42, c: 'bottleClear' as const, label: true  },
            { x: 684, h: 36, c: 'bottleGreen' as const, label: false },
            { x: 706, h: 42, c: 'bottleAmber' as const, label: true  },
            { x: 728, h: 36, c: 'bottleClear' as const, label: false },
            { x: 750, h: 42, c: 'bottleGreen' as const, label: false },
            { x: 772, h: 38, c: 'bottleAmber' as const, label: true  },
          ].map((b, i) => {
            const bodyY = 396 + (42 - b.h);
            return (
              <g key={`bg-r1-${i}`}>
                <rect x={b.x + 4} y={bodyY - 8} width="6" height="10" fill={`url(#${b.c})`} />
                <rect x={b.x + 3} y={bodyY - 12} width="8" height="5" fill="#a87a3a" stroke="#1a0e08" strokeWidth="0.5" />
                <rect x={b.x} y={bodyY} width="14" height={b.h} fill={`url(#${b.c})`} rx="1.5" />
                {b.label && (
                  <>
                    <rect x={b.x + 2} y={bodyY + b.h * 0.4} width="10" height="11" fill="#f3e3c2" opacity="0.75" />
                    <line x1={b.x + 4} y1={bodyY + b.h * 0.45} x2={b.x + 10} y2={bodyY + b.h * 0.45} stroke="#3a2410" strokeWidth="0.6" />
                    <line x1={b.x + 4} y1={bodyY + b.h * 0.5}  x2={b.x + 10} y2={bodyY + b.h * 0.5}  stroke="#3a2410" strokeWidth="0.6" />
                  </>
                )}
                <rect x={b.x + 1} y={bodyY + 4} width="1.5" height={b.h - 12} fill="#ffffff" opacity="0.2" />
              </g>
            );
          })}
          <rect x="750" y="396" width="14" height="42" fill="url(#bottleGreen)" />
          <ellipse cx="757" cy="394" rx="6" ry="3" fill="#1a1008" />
          {/* Mid shelf */}
          <rect x="616" y="456" width="16" height="42" fill="url(#bottleAmber)" />
          <ellipse cx="624" cy="454" rx="7" ry="3" fill="#1a1008" />
          <rect x="640" y="462" width="14" height="36" fill="url(#bottleGreen)" />
          <rect x="660" y="458" width="14" height="40" fill="url(#bottleClear)" />
          <rect x="680" y="466" width="16" height="32" fill="url(#bottleAmber)" />
          <rect x="704" y="458" width="14" height="40" fill="url(#bottleGreen)" />
          <rect x="724" y="464" width="14" height="34" fill="url(#bottleAmber)" />
          <rect x="746" y="458" width="16" height="40" fill="url(#bottleClear)" />
          <rect x="770" y="464" width="14" height="34" fill="url(#bottleGreen)" />
          {/* Bottom shelf — taller bottles + a barrel-shape cask. */}
          <rect x="618" y="512" width="18" height="46" fill="url(#bottleAmber)" />
          <rect x="640" y="514" width="16" height="44" fill="url(#bottleGreen)" />
          {/* Cask (barrel) on bottom shelf. */}
          <ellipse cx="690" cy="534" rx="22" ry="22" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1" />
          <rect x="668" y="514" width="44" height="40" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1" />
          <line x1="668" y1="524" x2="712" y2="524" stroke="#3a2410" strokeWidth="2" />
          <line x1="668" y1="544" x2="712" y2="544" stroke="#3a2410" strokeWidth="2" />
          <rect x="724" y="516" width="14" height="42" fill="url(#bottleClear)" />
          <rect x="746" y="514" width="16" height="44" fill="url(#bottleAmber)" />

          {/* Bar counter top. */}
          <rect x="560" y="620" width="240" height="20" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="2" />
          <rect x="560" y="620" width="240" height="3"  fill="#a87a3a" opacity="0.6" />

          {/* Bar counter front (kick-board). */}
          <rect x="560" y="640" width="240" height="180" fill="url(#oak)" stroke="#1a0e08" strokeWidth="2" />
          <rect x="560" y="800" width="240" height="20"  fill="#1a0e08" />
          {/* Counter front detailing. */}
          <line x1="640" y1="640" x2="640" y2="800" stroke="#1a0e08" strokeWidth="1" opacity="0.55" />
          <line x1="720" y1="640" x2="720" y2="800" stroke="#1a0e08" strokeWidth="1" opacity="0.55" />

          {/* Tankards on the bar. */}
          {/* Tankard A (left of bar). */}
          <rect x="588" y="588" width="24" height="32" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1.5" />
          <rect x="588" y="588" width="24" height="3" fill="#fff7c2" opacity="0.85" />
          <rect x="612" y="592" width="6" height="20" fill="none" stroke="#1a0e08" strokeWidth="1.5" />
          <rect x="608" y="592" width="6" height="20" fill="none" stroke="#3a2410" strokeWidth="1.5" />
          {/* Tankard B (mid). */}
          <rect x="664" y="592" width="22" height="28" fill="#5b3a1f" stroke="#1a0e08" strokeWidth="1.5" />
          <rect x="664" y="592" width="22" height="2" fill="#fff7c2" opacity="0.7" />
          <rect x="686" y="596" width="6" height="18" fill="none" stroke="#1a0e08" strokeWidth="1.5" />
          {/* Bar candle removed per user request — was distracting on menu. */}
        </g>

        {/* ─── Layer 7: HANGING LANTERNS ─────────────────────────────── */}
        <g className="bg-lights">
          {/* Lantern A — left, lower. */}
          <line x1="200" y1="0" x2="200" y2="160" stroke="#1a0e08" strokeWidth="1.5" />
          <ellipse cx="200" cy="200" rx="22" ry="32" fill="#1a0e08" stroke="#3a2410" strokeWidth="2" />
          <rect x="184" y="172" width="32" height="6" fill="#3a2410" />
          <rect x="190" y="232" width="20" height="4" fill="#3a2410" />
          {/* Lantern A flame. */}
          <ellipse cx="200" cy="200" rx="14" ry="22" fill="#ff8c42" opacity="0.65">
            <animate attributeName="opacity" values="0.5;0.95;0.65" dur="1.2s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="200" cy="202" rx="8" ry="14" fill="#fff7c2" opacity="0.9" />
          {/* Halo. */}
          <ellipse cx="200" cy="200" rx="80" ry="80" fill="url(#lanternHalo)" />

          {/* Lantern B — center, deeper recess. */}
          <line x1="400" y1="0" x2="400" y2="120" stroke="#1a0e08" strokeWidth="1.5" />
          <ellipse cx="400" cy="170" rx="26" ry="38" fill="#1a0e08" stroke="#3a2410" strokeWidth="2" />
          <rect x="380" y="138" width="40" height="6" fill="#3a2410" />
          <rect x="386" y="206" width="28" height="4" fill="#3a2410" />
          <ellipse cx="400" cy="170" rx="16" ry="26" fill="#ff8c42" opacity="0.7">
            <animate attributeName="opacity" values="0.5;0.95;0.65" dur="1.7s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="400" cy="172" rx="9" ry="16" fill="#fff7c2" opacity="0.9" />
          <ellipse cx="400" cy="170" rx="100" ry="100" fill="url(#lanternHalo)" />

          {/* Lantern C — right, lower. */}
          <line x1="600" y1="0" x2="600" y2="160" stroke="#1a0e08" strokeWidth="1.5" />
          <ellipse cx="600" cy="200" rx="22" ry="32" fill="#1a0e08" stroke="#3a2410" strokeWidth="2" />
          <rect x="584" y="172" width="32" height="6" fill="#3a2410" />
          <rect x="590" y="232" width="20" height="4" fill="#3a2410" />
          <ellipse cx="600" cy="200" rx="14" ry="22" fill="#ff8c42" opacity="0.65">
            <animate attributeName="opacity" values="0.5;0.95;0.65" dur="2.1s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="600" cy="202" rx="8" ry="14" fill="#fff7c2" opacity="0.9" />
          <ellipse cx="600" cy="200" rx="80" ry="80" fill="url(#lanternHalo)" />
        </g>

        {/* ─── Layer 8: WALL SCONCES ─────────────────────────────────── */}
        <g className="bg-sconces">
          {/* Sconce L — flanks the hearth. */}
          <rect x="232" y="404" width="16" height="8" fill="#1a0e08" />
          <rect x="236" y="412" width="8" height="14" fill="#1a0e08" />
          <rect x="234" y="426" width="12" height="6" fill="#3a2410" />
          {/* Candle. */}
          <rect x="237" y="406" width="6" height="-12" fill="#f3e3c2" transform="translate(0,8)" />
          <ellipse cx="240" cy="394" rx="3" ry="6" fill="#ffb27a" opacity="0.8">
            <animate attributeName="opacity" values="0.55;0.95;0.7" dur="1.4s" repeatCount="indefinite" />
            <animate attributeName="ry" values="6;7.5;5.5;6" dur="1.4s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="240" cy="396" rx="2" ry="4" fill="#fff7c2" opacity="0.9" />
          <ellipse cx="240" cy="396" rx="35" ry="35" fill="url(#sconceHalo)" />

          {/* Sconce R — also flanks hearth, mirrored. */}
          <rect x="552" y="404" width="16" height="8" fill="#1a0e08" />
          <rect x="556" y="412" width="8" height="14" fill="#1a0e08" />
          <rect x="554" y="426" width="12" height="6" fill="#3a2410" />
          <rect x="557" y="394" width="6" height="14" fill="#f3e3c2" />
          <ellipse cx="560" cy="394" rx="3" ry="6" fill="#ffb27a" opacity="0.8">
            <animate attributeName="opacity" values="0.55;0.95;0.7" dur="1.6s" repeatCount="indefinite" />
            <animate attributeName="ry" values="6;7.5;5.5;6" dur="1.6s" repeatCount="indefinite" />
          </ellipse>
          <ellipse cx="560" cy="396" rx="2" ry="4" fill="#fff7c2" opacity="0.9" />
          <ellipse cx="560" cy="396" rx="35" ry="35" fill="url(#sconceHalo)" />
        </g>

        {/* ─── Layer 9: DECORATIONS (clock, picture, skull) ──────────── */}
        <g className="bg-decor">
          {/* Pendulum clock — left wall. */}
          <rect x="92" y="448" width="56" height="120" fill="#3a2410" stroke="#1a0e08" strokeWidth="2" />
          {/* Clock face. */}
          <circle cx="120" cy="478" r="22" fill="#f3e3c2" stroke="#1a0e08" strokeWidth="2" />
          <circle cx="120" cy="478" r="20" fill="none" stroke="#a87a3a" strokeWidth="1" />
          <line x1="120" y1="478" x2="120" y2="464" stroke="#1a0e08" strokeWidth="2" />
          <line x1="120" y1="478" x2="130" y2="478" stroke="#1a0e08" strokeWidth="1.5" />
          {/* Pendulum window. */}
          <rect x="106" y="510" width="28" height="48" fill="#0a0604" />
          {/* Pendulum (animated rotate around clock body). */}
          <g className="bg-pendulum" transform="translate(120 510)">
            <line x1="0" y1="0" x2="0" y2="36" stroke="#3a2410" strokeWidth="1.5" />
            <circle cx="0" cy="42" r="6" fill="#a87a3a" stroke="#1a0e08" strokeWidth="1" />
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="-8;8;-8"
              dur="2s"
              additive="sum"
              repeatCount="indefinite"
            />
          </g>

          {/* Framed picture — between hearth and bar. */}
          <rect x="544" y="240" width="60" height="74" fill="url(#oak)" stroke="#1a0e08" strokeWidth="2" />
          <rect x="552" y="248" width="44" height="58" fill="#3a2a40" />
          <rect x="552" y="248" width="44" height="14" fill="#5a4a60" opacity="0.5" />
          <ellipse cx="574" cy="280" rx="10" ry="10" fill="#1a1218" />
          <line x1="544" y1="276" x2="604" y2="276" stroke="#1a0e08" strokeWidth="0.5" opacity="0.3" />

          {/* Deer skull above hearth. */}
          <ellipse cx="400" cy="226" rx="34" ry="26" fill="#e6d6c0" stroke="#1a0e08" strokeWidth="1.5" />
          <ellipse cx="386" cy="222" rx="4" ry="6" fill="#1a0e08" />
          <ellipse cx="414" cy="222" rx="4" ry="6" fill="#1a0e08" />
          <path
            d="M 374 218 Q 350 180 340 160 M 348 174 L 332 168 M 358 196 L 340 198"
            fill="none" stroke="#e6d6c0" strokeWidth="2.5"
          />
          <path
            d="M 426 218 Q 450 180 460 160 M 452 174 L 468 168 M 442 196 L 460 198"
            fill="none" stroke="#e6d6c0" strokeWidth="2.5"
          />
          <path d="M 392 244 L 386 252 L 394 254 L 400 248 L 406 254 L 414 252 L 408 244 Z" fill="#cab8a0" />
        </g>

        {/* ─── Layer 10: FOREGROUND TABLE EDGE ───────────────────────── */}
        <g className="bg-foreground">
          {/* Suggests the camera is sitting at a table looking into the room. */}
          <rect x="0"   y="1080" width="800" height="120" fill="#1a0e08" />
          <rect x="0"   y="1080" width="800" height="14"  fill="#3a2410" />
          <rect x="0"   y="1078" width="800" height="6"   fill="#5b3a1f" />
          <rect x="0"   y="1076" width="800" height="2"   fill="#7a4a28" />
          {/* Slight wood grain on the table edge. */}
          <line x1="0" y1="1090" x2="800" y2="1090" stroke="#1a0e08" strokeWidth="0.6" opacity="0.7" />
          <line x1="0" y1="1098" x2="800" y2="1098" stroke="#3a2410" strokeWidth="0.4" opacity="0.5" />
        </g>

        {/* ─── Layer 11: TOP FOG ─────────────────────────────────────── */}
        <rect x="0" y="0" width="800" height="300" fill="url(#bgTopFog)" />

        {/* ─── Layer 12: PER-MODE ACCENT ─────────────────────────────── */}
        {mode === 'gate' && (
          <g className="bg-accent bg-accent-gate">
            {/* Receding wooden door silhouette behind the gate's foreground door. */}
            <rect x="320" y="660" width="160" height="200" fill="#1a0e08" opacity="0.55" />
            <rect x="324" y="664" width="152" height="192" fill="none" stroke="#3a2410" strokeWidth="1" opacity="0.55" />
          </g>
        )}
        {mode === 'select' && (
          <g className="bg-accent bg-accent-select">
            {/* Row of small framed silhouettes hung between hearth + bar (back wall, low alpha). */}
            <rect x="40"  y="260" width="34" height="46" fill="url(#oak)" opacity="0.35" />
            <rect x="46"  y="266" width="22" height="34" fill="#1a0e1f" opacity="0.5" />
            <rect x="86"  y="270" width="34" height="46" fill="url(#oak)" opacity="0.35" />
            <rect x="92"  y="276" width="22" height="34" fill="#1a0e1f" opacity="0.5" />
            <rect x="150" y="266" width="34" height="46" fill="url(#oak)" opacity="0.35" />
            <rect x="156" y="272" width="22" height="34" fill="#1a0e1f" opacity="0.5" />
          </g>
        )}
        {mode === 'home' && (
          <g className="bg-accent bg-accent-home">
            {/* Chalkboard hung above the bar. */}
            <rect x="620" y="350" width="160" height="40" fill="#1a1f24" stroke="#5b3a1f" strokeWidth="2" />
            <line x1="640" y1="364" x2="700" y2="362" stroke="#cab8a0" strokeWidth="1" opacity="0.65" />
            <line x1="640" y1="372" x2="720" y2="370" stroke="#cab8a0" strokeWidth="1" opacity="0.55" />
            <line x1="640" y1="380" x2="690" y2="378" stroke="#cab8a0" strokeWidth="1" opacity="0.55" />
            {/* Two rope hangers. */}
            <line x1="640" y1="350" x2="640" y2="332" stroke="#1a0e08" strokeWidth="1" />
            <line x1="760" y1="350" x2="760" y2="332" stroke="#1a0e08" strokeWidth="1" />
          </g>
        )}
        {mode === 'locker' && (
          <g className="bg-accent bg-accent-locker">
            {/* Cabinet door slightly ajar in the back-left wall. */}
            <rect x="80"  y="560" width="72" height="120" fill="url(#oak)" opacity="0.7" stroke="#1a0e08" strokeWidth="2" />
            <rect x="84"  y="566" width="64" height="108" fill="none" stroke="#3a2410" strokeWidth="1" opacity="0.7" />
            <circle cx="142" cy="618" r="3" fill="#a87a3a" />
            {/* Slight crack of warm light from inside. */}
            <rect x="148" y="566" width="3" height="108" fill="#ff8c42" opacity="0.55" />
          </g>
        )}
        {mode === 'onboarding' && (
          <g className="bg-accent bg-accent-onboarding">
            {/* Drawn-back curtains at the doorway. */}
            <path d="M 280 0 Q 310 240 340 380 L 310 380 Q 290 240 270 0 Z" fill="#3d1428" opacity="0.55" />
            <path d="M 520 0 Q 490 240 460 380 L 490 380 Q 510 240 530 0 Z" fill="#3d1428" opacity="0.55" />
          </g>
        )}

        {/* COBWEBS (2026-05-16 quality pass) — upper corners,
         *  low-opacity radial threads for hand-laid atmosphere. */}
        <g className="bg-cobwebs" opacity="0.3" stroke="#9a8a72" strokeWidth="0.6" fill="none">
          <path d="M 0 0 L 90 70" />
          <path d="M 0 35 L 90 70" />
          <path d="M 0 70 L 90 70" />
          <path d="M 45 0 L 90 70" />
          <path d="M 80 0 L 90 70" />
          <path d="M 16 22 Q 42 32 80 18" />
          <path d="M 10 44 Q 42 50 80 44" />
          <path d="M 8 64 Q 44 68 86 66" />
          <path d="M 800 0 L 710 70" />
          <path d="M 800 35 L 710 70" />
          <path d="M 800 70 L 710 70" />
          <path d="M 755 0 L 710 70" />
          <path d="M 720 0 L 710 70" />
          <path d="M 784 22 Q 758 32 720 18" />
          <path d="M 790 44 Q 758 50 720 44" />
          <path d="M 792 64 Q 756 68 714 66" />
        </g>

        {/* SOFT LIGHT BEAMS (2026-05-16 quality pass) — diagonal warm
         *  streaks falling from the hanging lanterns toward the floor.
         *  Drawn ABOVE props but BELOW the top fog so they fade. */}
        <g className="bg-beams" opacity="0.06">
          <path d="M 180 240 L 140 1200 L 220 1200 L 180 240 Z" fill="#ffb27a" />
          <path d="M 400 220 L 360 1200 L 440 1200 L 400 220 Z" fill="#ffb27a" />
          <path d="M 620 240 L 580 1200 L 660 1200 L 620 240 Z" fill="#ffb27a" />
        </g>

        {/* Vignette (2026-05-16) — soft darkening at edges to focus
         *  attention on the hearth + UI overlay. */}
        <radialGradient id="bgVignette" cx="0.5" cy="0.4" r="0.85">
          <stop offset="55%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.55" />
        </radialGradient>
        <rect x="0" y="0" width="800" height="1200" fill="url(#bgVignette)" />
      </svg>
      <canvas ref={canvasRef} className="bg-canvas" />
    </div>
  );
}
